import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import { getValueFormatter } from 'ui/AgGridReact/formatters';
import {
    CellClassFunc,
    ColDef,
    ColumnState,
    ColumnValueChangedEvent,
    ExcelExportParams,
    ExcelStyle,
    FilterChangedEvent,
    FilterModel,
    GridApi,
    GridReadyEvent,
    IsGroupOpenByDefaultParams,
    RowGroupingDisplayType,
    RowGroupOpenedEvent,
} from 'ag-grid-community';
import { PivotFieldsAggridT, ExpandedRowT } from 'business/definitions';
import { useFlatExport } from 'routes/BudgetModule/helpers/hooks/useFlatExport';
import { useMustRowBeExpanded } from './hooks';
import {
    addNodeToExpandedRows,
    getActualExpandedTree,
    removeNodeFromExpandedRows,
} from './helpers/expandRowsHelpers';
import { exportPivotGridAsExcel } from './helpers/exportPivotGridHelpers';
import { AgGridReactProps } from 'ag-grid-react';
import { CellRendererParams } from 'routes/BudgetModule/config/types';
import {
    coldefsSortMatches,
    getOrderedColdefs,
    updateColdefsOrder,
    updateColStateOrder,
} from './helpers/sortColumnsHelper';
import { trackEvent } from 'helpers/trackerHelpers';

type PivotGridProps = {
    data: ResponseDataT[];
    initialColDefs: ColDef[];
    pivotFieldsAggrid: PivotFieldsAggridT | null | undefined;
    updateGridFilters?: (event: FilterChangedEvent) => void;
    gridFilters?: FilterModel;
    onColumnsChanged: (pivotFieldsAggridT: PivotFieldsAggridT) => void;
    gridProps: Partial<AgGridReactProps>;
    className?: string;
    setExporterToExcel?: (arg1: () => void) => void;
    groupIncludeFooter?: boolean;
    groupIncludeTotalFooter?: boolean;
    groupHideOpenParents?: boolean;
    groupDisplayType?: RowGroupingDisplayType;
    onModelUpdate?: () => void;
    excelStyles?: ExcelStyle[];
    defaultExcelExportParams?: ExcelExportParams;
    getGroupRowCellClass?: CellClassFunc;
};

const ROW_HEIGHT_PX = 32;

const DEFAULT_COL_DEF = {
    flex: 1,
    minWidth: 80,
    sortable: true,
    resizable: true,
    suppressMovable: true,
    suppressFiltersToolPanel: true,
};

const PivotGrid = ({
    data,
    initialColDefs,
    pivotFieldsAggrid,
    updateGridFilters,
    gridFilters,
    onColumnsChanged = () => {},
    gridProps = {},
    className = '',
    setExporterToExcel = () => {},
    groupIncludeFooter = false,
    groupIncludeTotalFooter = true,
    groupHideOpenParents = true,
    groupDisplayType = undefined,
    onModelUpdate = () => {},
    excelStyles = [],
    defaultExcelExportParams = {},
    getGroupRowCellClass = () => null,
}: PivotGridProps): React.ReactElement => {
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    const exportFunction = () => exportPivotGridAsExcel(gridApi);
    const { shouldShowOpenedGroups, setShouldShowOpenedGroups } =
        useFlatExport(exportFunction);

    const { t } = useTranslation();

    const memoColDefs = useMemo(
        () => getOrderedColdefs(initialColDefs, pivotFieldsAggrid?.colDefs),
        [JSON.stringify(initialColDefs)],
    );

    const mustRowBeExpanded = useMustRowBeExpanded();

    /** Should the gridProps change, we force a cell refresh */
    // TODO: do we still need it? It's 3 years old and it looks hacky
    useEffect(() => {
        if (gridApi !== null) {
            gridApi.refreshCells({ force: true });
        }
    }, [JSON.stringify(gridProps)]);

    const applyUserGridSettings = (agGridApi: GridApi) => {
        // we need to apply column state based on our own defined col def to override ag-grid initially calculated column state
        // which is not handling pivot columns like we want. This is definitely hacky, we should look for a better solution.
        if (pivotFieldsAggrid?.colDefs) {
            agGridApi.applyColumnState({
                state: pivotFieldsAggrid?.colDefs as ColumnState[],
            });
        }
        if (gridFilters !== undefined) {
            agGridApi.setFilterModel(gridFilters.analyseGridFilters);
        }
    };

    const onGridReady = useCallback(
        (params: GridReadyEvent) => {
            setGridApi(params.api);
            setExporterToExcel(() => () => setShouldShowOpenedGroups(true));
            applyUserGridSettings(params.api);
        },
        // we don't need to refresh this callback when pivotFieldsAggrid changes
        [],
    );

    const onUpdateExpandedRows = useCallback(
        (newExpandedRows: ExpandedRowT[]) => {
            const colDefs = gridApi
                ? gridApi.getColumnState()
                : pivotFieldsAggrid?.colDefs || [];
            const newState: PivotFieldsAggridT = {
                // @ts-expect-error format colDefs to only one type
                colDefs,
                expandedRows: newExpandedRows,
            };
            onColumnsChanged(newState);
        },
        [onColumnsChanged, gridApi, pivotFieldsAggrid?.colDefs],
    );

    const storeModifiedExpandedRows = useCallback(
        (event: RowGroupOpenedEvent) => {
            const { node } = event;
            let currentExpandedRows = pivotFieldsAggrid?.expandedRows || [];

            currentExpandedRows = node.expanded
                ? addNodeToExpandedRows(currentExpandedRows, node)
                : removeNodeFromExpandedRows(currentExpandedRows, node);

            onUpdateExpandedRows(currentExpandedRows);
        },
        [pivotFieldsAggrid?.expandedRows, onUpdateExpandedRows],
    );

    const storeActualExpandedTree = useCallback(() => {
        const newExpandedRows = getActualExpandedTree(gridApi);
        onUpdateExpandedRows(newExpandedRows);
    }, [gridApi, onUpdateExpandedRows]);

    const onFiltersChanged = (event: FilterChangedEvent) => {
        if (updateGridFilters !== undefined) {
            updateGridFilters(event);
        }
    };

    const updateColumns = useCallback(
        (reinitializeExpandedRows: boolean) => {
            // when ag-grid init, it triggers events with empty columns definitions,
            // we don't want them to reset our settings
            if (gridApi === null) return;

            const newColDefs = gridApi.getColumnState();
            if (!_.isEqual(pivotFieldsAggrid?.colDefs, newColDefs)) {
                const newExpandedRows = reinitializeExpandedRows
                    ? getActualExpandedTree(gridApi)
                    : pivotFieldsAggrid?.expandedRows || [];

                const newState: PivotFieldsAggridT = {
                    // @ts-expect-error format colDefs to only one type
                    colDefs: newColDefs,
                    expandedRows: newExpandedRows,
                };

                onColumnsChanged(newState);
            }
        },
        [gridApi, pivotFieldsAggrid, onColumnsChanged],
    );

    const updateColumnsOnly = useCallback(
        () => updateColumns(false),
        [updateColumns],
    );

    const updateColumnsAndReinitializeExpandedRows = useCallback(
        () => updateColumns(true),
        [updateColumns],
    );

    const isGroupOpenByDefault = useCallback(
        (params: IsGroupOpenByDefaultParams): boolean =>
            pivotFieldsAggrid?.expandedRows !== null &&
            pivotFieldsAggrid?.expandedRows !== undefined &&
            mustRowBeExpanded(params.rowNode, pivotFieldsAggrid.expandedRows),
        [mustRowBeExpanded, pivotFieldsAggrid],
    );

    const memoData = React.useMemo(() => data, [JSON.stringify(data)]);

    const memoGridProps = React.useMemo(
        () => gridProps,
        [JSON.stringify(gridProps)],
    );

    const autoGroupColumnDef: ColDef = React.useMemo(
        () => ({
            minWidth: 140,
            cellRendererParams: {
                totalValueGetter: (params: CellRendererParams) => {
                    const valueFormatter = getValueFormatter(params);
                    const formattedValue = valueFormatter
                        ? valueFormatter(params)
                        : params.value;
                    return `${t('common.total')} ${
                        formattedValue && formattedValue !== '-'
                            ? formattedValue
                            : ''
                    }`;
                },
                suppressCount: true,
            },
            cellClass: getGroupRowCellClass,
        }),
        [],
    );

    const onColumnValueChanged = useCallback(
        (event: ColumnValueChangedEvent) => {
            updateColumnsOnly();
            const newColOrder = event.api
                .getValueColumns()
                .map((col) => col.getColId());
            const apiColdefs = event.api.getColumnDefs();
            if (!coldefsSortMatches(apiColdefs!, newColOrder)) {
                trackEvent('pivotGrid/value-column-order/changed');
                const updatedColdefs = updateColdefsOrder(
                    apiColdefs!,
                    newColOrder,
                );
                const colstate = event.api.getColumnState();
                const updatedColState = updateColStateOrder(
                    colstate,
                    newColOrder,
                );

                //only way to update columnDefs
                event.api.setGridOption('columnDefs', []);
                event.api.setGridOption('columnDefs', updatedColdefs);

                //force apply new sort
                event.api.applyColumnState({
                    state: updatedColState,
                    applyOrder: true,
                });
            }
        },
        [updateColumnsOnly],
    );

    return (
        <AgGridReact
            className={`ag-theme-material ${className}`}
            defaultColDef={DEFAULT_COL_DEF}
            rowHeight={ROW_HEIGHT_PX}
            headerHeight={ROW_HEIGHT_PX}
            rowData={memoData}
            pivotMode
            autoGroupColumnDef={autoGroupColumnDef}
            groupDisplayType={groupDisplayType}
            groupHideOpenParents={groupHideOpenParents}
            sideBar
            onGridReady={onGridReady}
            onSortChanged={updateColumnsOnly}
            onColumnValueChanged={onColumnValueChanged}
            onColumnRowGroupChanged={() => {
                updateColumnsAndReinitializeExpandedRows();
                onModelUpdate();
            }}
            onColumnPivotChanged={() => {
                updateColumnsAndReinitializeExpandedRows();
                onModelUpdate();
            }}
            onColumnMoved={updateColumnsOnly}
            onGridColumnsChanged={updateColumnsAndReinitializeExpandedRows}
            onColumnResized={updateColumnsOnly}
            onRowGroupOpened={storeModifiedExpandedRows}
            onExpandOrCollapseAll={storeActualExpandedTree}
            isGroupOpenByDefault={isGroupOpenByDefault}
            showOpenedGroup={shouldShowOpenedGroups}
            onFilterChanged={onFiltersChanged}
            columnDefs={memoColDefs}
            groupTotalRow={groupIncludeFooter ? 'bottom' : undefined}
            grandTotalRow={groupIncludeTotalFooter ? 'bottom' : undefined}
            excelStyles={excelStyles}
            defaultExcelExportParams={defaultExcelExportParams}
            {...memoGridProps}
        />
    );
};

export default React.memo(PivotGrid);
